import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../components/Header';
import { EVENT_GENRE_OPTIONS } from '../constants/eventGenres';
import styles from './CreateEventPage.module.css';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import MapLocationPicker from '../components/MapLocationPicker';

const EventEditPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useTranslation('createEvent');
  const [loadingAuth, setLoadingAuth] = useState(true);
  const createEmptyArtist = () => ({ id: '', name: '', description: '', imageFile: null, imagePreview: '', existingImageUrl: '', existingImagePath: '', group_key: null, sort_order: 0 });

  const [form, setForm] = useState({
    name: '', date: '', end_date: '', open_time: '', start_time: '', end_time: '',
    slug: '', genre: '', address: '', price: '', description: '', contact_info: '', venue_id: '', venue_name: '', fee_bearer: 'organizer',
    event_type: 'paid', donation_enabled: false, donation_start_at: '', donation_end_at: '',
    lat: '', lng: '',
  });
  const [artists, setArtists] = useState([createEmptyArtist()]);
  const [performerGroups, setPerformerGroups] = useState([]);
  const [flyers, setFlyers] = useState([]);
  const [existingFlyers, setExistingFlyers] = useState([]);
  const flyerInputRef = useRef(null);
  const [pendingFlyers, setPendingFlyers] = useState([]);
  const [pendingDeleteIds, setPendingDeleteIds] = useState(new Set());
  const [venueSearch, setVenueSearch] = useState('');
  const [venueResults, setVenueResults] = useState([]);
  const [artistSearch, setArtistSearch] = useState('');
  const [artistResults, setArtistResults] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [ticketPrices, setTicketPrices] = useState([]);
  const [flyerPreviews, setFlyerPreviews] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const normalizeNullableText = (value) => {
    if (value === null || value === undefined) return '';
    const text = String(value);
    if (text.trim().toLowerCase() === 'null') return '';
    return text;
  };

  const humanizeValidationMessage = (key, message) => {
    const msg = String(message || '');
    if (key === 'contact_info') {
      if (msg.includes('required')) return t('errContactRequired');
      return t('errContactCheck');
    }
    if (/^artist_names\.\d+$/.test(key)) {
      if (msg.includes('required')) return t('errArtistNameRequired');
      return t('errArtistNameCheck');
    }
    if (/^artist_images\.\d+$/.test(key)) return t('errArtistImage');
    if (/^flyers\.\d+$/.test(key)) return t('errFlyer');
    return msg;
  };

  useEffect(() => {
    if (!success) return undefined;
    const timerId = setTimeout(() => {
      navigate('/dashboard');
    }, 2500);

    return () => clearTimeout(timerId);
  }, [success, navigate]);

  const getFieldError = (...names) => {
    for (const name of names) {
      const msg = fieldErrors?.[name]?.[0];
      if (msg) return msg;
    }
    return '';
  };

  const getErrorSummary = () => {
    const messages = Object.values(fieldErrors || {}).flat().map((msg) => String(msg));
    if (messages.length === 0) return '';
    return Array.from(new Set(messages)).join(' / ');
  };

  const clearFieldError = (...names) => {
    setFieldErrors((prev) => {
      const next = { ...prev };
      let changed = false;
      names.forEach((name) => {
        if (next[name]) {
          delete next[name];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  };

  const isPastEvent = (dateValue, endDateValue) => {
    const referenceDate = endDateValue || dateValue;
    if (!referenceDate) return false;
    return new Date(referenceDate) < new Date(new Date().toDateString());
  };

  const getDisplayValue = (value, fallback = t('fallbackNoInput')) => {
    const text = String(value ?? '').trim();
    return text || fallback;
  };

  const getArtistSummary = () => {
    const names = artists
      .map((artist) => String(artist?.name || '').trim())
      .filter(Boolean);
    return names.length > 0 ? names.join(' / ') : t('artistNotSet');
  };

  const getTicketSummary = () => {
    if (form.event_type === 'free') {
      return [t('ticketFree')];
    }

    const lines = ticketPrices
      .filter((group) => String(group?.type || '').trim() !== '' || (group?.prices || []).length > 0)
      .map((group) => {
        const groupName = getDisplayValue(group?.type, t('ticketGroupUnnamed'));
        const priceNames = (group?.prices || [])
          .map((price) => {
            const type = getDisplayValue(price?.price_type, t('ticketTypeUnnamed'));
            const amount = String(price?.price ?? '').trim();
            return amount !== '' ? `${type} ${amount}${t('ticketSuffix')}` : type;
          })
          .join(' / ');
        return priceNames ? `${groupName}: ${priceNames}` : groupName;
      });

    return lines.length > 0 ? lines : [t('fallbackNoValue')];
  };

  const getConfirmSections = () => ([
    { label: t('confirmEventName'), value: getDisplayValue(form.name) },
    { label: t('confirmGenre'), value: getDisplayValue(form.genre) },
    { label: t('confirmDate'), value: getDisplayValue(form.date) },
    { label: t('confirmEndDate'), value: getDisplayValue(form.end_date) },
    { label: t('confirmTime'), value: `${getDisplayValue(form.open_time)} / ${getDisplayValue(form.start_time)} / ${getDisplayValue(form.end_time)}` },
    { label: t('confirmVenue'), value: getDisplayValue(form.venue_name) },
    { label: t('confirmAddress'), value: getDisplayValue(form.address) },
    { label: t('confirmContact'), value: getDisplayValue(form.contact_info) },
    { label: t('confirmEventType'), value: form.event_type === 'free' ? t('confirmEventTypeFree') : t('confirmEventTypePaid') },
    { label: t('confirmDonation'), value: form.donation_enabled ? `${t('confirmDonationEnabled')}${form.donation_start_at ? t('confirmDonationStart', { date: form.donation_start_at }) : ''}` : t('confirmDonationDisabled') },
    { label: t('confirmTickets'), value: getTicketSummary().join('\n') },
    { label: t('confirmArtists'), value: getArtistSummary() },
    { label: t('confirmFlyers'), value: t('confirmFlyerCount', { count: (existingFlyers.length - pendingDeleteIds.size) + pendingFlyers.length }) },
  ]);

  const validateBeforePublish = () => {
    setFieldErrors({});
    setSuccess('');

    if ((!existingFlyers || existingFlyers.length - pendingDeleteIds.size <= 0) && pendingFlyers.length === 0) {
      setFieldErrors({ flyers: [t('errFlyerEditRequired')] });
      return false;
    }

    return true;
  };

  const submitPublish = async () => {
    setIsSubmitting(true);
    try {
      // 削除マーク済みフライヤーを先に削除
      for (const imageId of pendingDeleteIds) {
        await axios.delete(`${process.env.REACT_APP_API_URL}/event-images/${imageId}`);
      }
      setExistingFlyers(prev => prev.filter(f => !pendingDeleteIds.has(f.id)));
      setPendingDeleteIds(new Set());

      // 未アップロードのフライヤーを送信
      for (const { file } of pendingFlyers) {
        const fd = new FormData();
        fd.append('type', 'flyer');
        fd.append('image', file);
        const res = await axios.post(`${process.env.REACT_APP_API_URL}/events/${id}/images`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const newImage = res.data?.image || null;
        if (newImage) setExistingFlyers(prev => [...prev, newImage]);
      }
      setPendingFlyers([]);
      const formData = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (key === 'price') {
          formData.append(key, value || 0);
        } else {
          formData.append(key, value);
        }
      });
      performerGroups.forEach((g, i) => {
        formData.append(`performer_groups[${i}][key]`, g.key);
        formData.append(`performer_groups[${i}][name]`, g.name);
        formData.append(`performer_groups[${i}][sort_order]`, g.sort_order ?? i);
      });
      artists.forEach((artist, i) => {
        formData.append(`artist_ids[${i}]`, artist.id || '');
        formData.append(`artist_names[${i}]`, artist.name);
        formData.append(`artist_descriptions[${i}]`, artist.id ? '' : (artist.description || ''));
        formData.append(`artist_existing_image_paths[${i}]`, artist.id ? '' : (artist.existingImagePath || ''));
        formData.append(`artist_group_keys[${i}]`, artist.group_key || '');
        formData.append(`artist_sort_orders[${i}]`, artist.sort_order ?? i);
        if (!artist.id && artist.imageFile) {
          formData.append(`artist_images[${i}]`, artist.imageFile);
        }
      });
      if (form.event_type !== 'free') {
        ticketPrices.forEach((tp, i) => {
          if (tp.id) formData.append(`price_groups[${i}][id]`, String(tp.id));
          formData.append(`price_groups[${i}][type]`, tp.type);
          formData.append(`price_groups[${i}][price_date]`, tp.price_date);
          formData.append(`price_groups[${i}][price_end_date]`, tp.price_end_date);
          formData.append(`price_groups[${i}][ticket_limit]`, tp.ticket_limit || '');
          tp.prices.forEach((pt, j) => {
            if (pt.id) formData.append(`price_groups[${i}][prices][${j}][id]`, String(pt.id));
            formData.append(`price_groups[${i}][prices][${j}][price_type]`, pt.price_type);
            formData.append(`price_groups[${i}][prices][${j}][price]`, pt.price);
            formData.append(`price_groups[${i}][prices][${j}][ticket_limit]`, pt.ticket_limit || '');
            if (pt.price_type === 'グループ') {
              formData.append(`price_groups[${i}][prices][${j}][min_people]`, pt.min_people || '');
            }
          });
        });
      }
      formData.set('event_type', form.event_type || 'paid');
      formData.set('donation_enabled', form.donation_enabled ? '1' : '0');
      formData.set('donation_start_at', form.donation_start_at || '');
      formData.set('donation_end_at', form.donation_end_at || '');
      await axios.post(`${process.env.REACT_APP_API_URL}/events/${id}?_method=PUT`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setIsConfirmOpen(false);
      setSuccess(t('successUpdate'));
    } catch (err) {
      const data = err?.response?.data;
      if (data?.errors && typeof data.errors === 'object') {
        const normalizedFieldErrors = {};
        Object.entries(data.errors).forEach(([key, value]) => {
          const rawMessages = Array.isArray(value) ? value : [value];
          const messages = rawMessages.map((msg) => humanizeValidationMessage(key, msg));
          normalizedFieldErrors[key] = messages;
        });
        setFieldErrors(normalizedFieldErrors);
      } else if (data?.message) {
        setFieldErrors({ submit: [String(data.message)] });
      } else {
        setFieldErrors({ submit: [t('errUpdate')] });
      }
      setIsConfirmOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    // 編集権限ガード
    const fetchEvent = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/my-events/${id}/status`);
        const eventData = res.data.event;
        // 本人チェック（主催者IDとログインユーザーIDが一致しなければリダイレクト）
        if (!user || eventData.organizer_id !== user.id) {
          navigate('/');
          return;
        }
        setForm({
          ...eventData,
          date:               eventData.date?.slice(0, 10)       || '',
          end_date:           eventData.end_date?.slice(0, 10)   || '',
          open_time:          eventData.open_time?.slice(0, 5)   || '',
          start_time:         eventData.start_time?.slice(0, 5)  || '',
          end_time:           eventData.end_time?.slice(0, 5)    || '',
          genre:              eventData.genre                    || '',
          venue_name:         eventData.venue                    || '',
          description:        normalizeNullableText(eventData.description),
          contact_info:       normalizeNullableText(eventData.contact_info),
          fee_bearer:         eventData.fee_bearer               || 'organizer',
          event_type:         eventData.event_type               || 'paid',
          donation_enabled:   eventData.donation_enabled         ?? false,
          donation_start_at:  eventData.donation_start_at ? String(eventData.donation_start_at).slice(0, 16) : '',
          donation_end_at:    eventData.donation_end_at   ? String(eventData.donation_end_at).slice(0, 16)   : '',
          lat:                eventData.lat != null ? String(eventData.lat) : '',
          lng:                eventData.lng != null ? String(eventData.lng) : '',
        });
        setArtists((eventData.event_artists || []).map(a => ({
          ...createEmptyArtist(),
          id: a.artist_id || '',
          name: a.artist_name || '',
          description: a.artist_description || '',
          existingImageUrl: a.artist_profile_image_url || '',
          existingImagePath: a.custom_profile_image_path || '',
          group_key: a.group_key || null,
          sort_order: a.sort_order ?? 0,
        })));
        if (Array.isArray(eventData.performer_groups)) {
          setPerformerGroups(eventData.performer_groups.map(g => ({
            key: g.key || ('group-' + g.id),
            id: g.id || null,
            name: g.name || '',
            sort_order: g.sort_order ?? 0,
          })));
        }
        setExistingFlyers(eventData.flyers || []);
        // 新API: price_groups をそのまま利用
        setTicketPrices((eventData.price_groups || []).map(g => ({
          // 実販売（売上/予約）が1件でもあれば開始済みとみなす
          hasActualSales: (g.prices || []).some((pt) => Number(pt.sold_count || 0) > 0 || Number(pt.reserved_count || 0) > 0),
          id: g.id,
          type: g.type || '',
          price_date: g.price_date || '',
          price_end_date: g.price_end_date || '',
          ticket_limit: g.ticket_limit || '',
          prices: (g.prices || []).map(pt => ({
            id: pt.id,
            price_type: pt.price_type || '',
            price: pt.price || '',
            ticket_limit: pt.ticket_limit || '',
            min_people: pt.min_people || ''
          }))
        })));
      } catch (err) {
        setFieldErrors({ submit: [t('errEventLoad')] });
      } finally {
        setLoadingAuth(false);
      }
    };
    if (user) {
      fetchEvent();
    }
  }, [id, user, navigate]);

  useEffect(() => {
    if (!venueSearch || typeof venueSearch !== 'string' || venueSearch.trim() === '') return;
    axios.get(`${process.env.REACT_APP_API_URL}/search-venues?query=${venueSearch}`)
      .then(res => setVenueResults(res.data))
      .catch(() => setVenueResults([]));
  }, [venueSearch]);

  useEffect(() => {
    if (artistSearch.trim() === '') return;
    axios.get(`${process.env.REACT_APP_API_URL}/search-users?role=artist&query=${artistSearch}`)
      .then(res => setArtistResults(res.data))
      .catch(() => setArtistResults([]));
  }, [artistSearch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'venue_name') {
      setForm(prev => ({ ...prev, venue_name: value, venue_id: '' }));
    } else if (name === 'date' || name === 'end_date') {
      const nextDate = name === 'date' ? value : form.date;
      const nextEndDate = name === 'end_date' ? value : form.end_date;
      const isPast = isPastEvent(nextDate, nextEndDate);
      setForm(prev => ({
        ...prev,
        [name]: value,
        ...(isPast ? { event_type: 'free', donation_enabled: false } : {}),
      }));
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
    clearFieldError(name, 'submit');
  };

  // フライヤー: ファイル選択時はローカル保持のみ（送信時にアップロード）
  const resizeFlyerIfNeeded = async (file) => {
    const MAX = 3000;
    try {
      const bitmap = await createImageBitmap(file);
      const { width: w, height: h } = bitmap;
      if (w <= MAX && h <= MAX) {
        bitmap.close();
        return file;
      }
      const scale = MAX / Math.max(w, h);
      const canvas = document.createElement('canvas');
      canvas.width  = Math.round(w * scale);
      canvas.height = Math.round(h * scale);
      const ctx = canvas.getContext('2d');
      if (!ctx) { bitmap.close(); return file; }
      ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      bitmap.close();
      const mimeType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
      return await new Promise((resolve) => {
        canvas.toBlob((blob) => {
          if (!blob) { resolve(file); return; }
          resolve(new File([blob], file.name, { type: mimeType, lastModified: file.lastModified }));
        }, mimeType, 0.92);
      });
    } catch (e) {
      console.warn('[flyer resize] failed, using original:', e);
      return file;
    }
  };

  const handleFlyerUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    clearFieldError('flyers');
    const resized = await Promise.all(files.map(resizeFlyerIfNeeded));
    const newEntries = resized.map(file => ({ file, previewUrl: URL.createObjectURL(file) }));
    setPendingFlyers(prev => [...prev, ...newEntries]);
    if (flyerInputRef.current) flyerInputRef.current.value = '';
  };

  // 未送信フライヤー: キャンセル
  const handleRemovePendingFlyer = (index) => {
    setPendingFlyers(prev => {
      URL.revokeObjectURL(prev[index].previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  // フライヤー: 削除マーク（送信時に実行）
  const handleDeleteFlyer = (imageId) => {
    setPendingDeleteIds(prev => new Set([...prev, imageId]));
  };

  // フライヤー: 削除マーク取り消し
  const handleCancelDelete = (imageId) => {
    setPendingDeleteIds(prev => {
      const next = new Set(prev);
      next.delete(imageId);
      return next;
    });
  };

  // 価格帯（期間）追加・削除・編集
  const handleTicketPriceChange = (idx, field, value) => {
    setTicketPrices(prices => prices.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  };
  const addTicketPrice = () => {
    setTicketPrices(prices => [...prices, {
      hasActualSales: false,
      type: '',
      price_date: '',
      price_end_date: '',
      ticket_limit: '',
      prices: [ { price_type: '通常', price: '', ticket_limit: '' } ]
    }]);
  };
  const removeTicketPrice = (idx) => {
    setTicketPrices(prices => prices.filter((_, i) => i !== idx));
  };
  // 価格タイプ追加・削除・編集（各期間ごと）
  const handlePriceTypeChange = (periodIdx, priceIdx, field, value) => {
    setTicketPrices(prices => prices.map((p, i) =>
      i === periodIdx
        ? { ...p, prices: p.prices.map((pt, j) => j === priceIdx ? { ...pt, [field]: value } : pt) }
        : p
    ));
  };
  const addPriceType = (periodIdx, price_type) => {
    setTicketPrices(prices => prices.map((p, i) =>
      i === periodIdx
        ? { ...p, prices: [...p.prices, { price_type, price: '', ticket_limit: '', min_people: '' }] }
        : p
    ));
  };
  const removePriceType = (periodIdx, priceIdx) => {
    setTicketPrices(prices => prices.map((p, i) =>
      i === periodIdx
        ? { ...p, prices: p.prices.filter((_, j) => j !== priceIdx) }
        : p
    ));
  };

  const addArtistToList = (artist) => {
    if (!artist) return;
    if (artist.id && artists.some((a) => a.id === artist.id)) {
      return;
    }
    const normalizedArtist = {
      ...createEmptyArtist(),
      ...artist,
    };
    {
      setArtists(prev => {
        const copy = [...prev];
        const emptyIndex = copy.findIndex(a => !a.name || (typeof a.name === 'string' && a.name.trim() === ''));
        if (emptyIndex !== -1) {
          copy[emptyIndex] = normalizedArtist;
        } else {
          copy.push(normalizedArtist);
        }
        return copy;
      });
    }
  };

  const handleArtistValueChange = (index, field, value) => {
    setArtists(prev => prev.map((artist, i) => {
      if (i !== index) return artist;

      if (field === 'name') {
        return {
          ...artist,
          name: value,
          id: '',
          existingImageUrl: artist.id ? '' : artist.existingImageUrl,
        };
      }

      return {
        ...artist,
        [field]: value,
      };
    }));
  };

  const handleArtistImageChange = (index, file) => {
    setArtists(prev => prev.map((artist, i) => {
      if (i !== index) return artist;

      return {
        ...artist,
        imageFile: file || null,
        imagePreview: file ? URL.createObjectURL(file) : '',
      };
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!validateBeforePublish()) {
      return;
    }

    setIsConfirmOpen(true);
  };

  if (!form || loadingAuth) return <p>{t('loadingEvent')}</p>;

  return (
    <>
      <Header />
      <div className={styles.container}>
        <h2 className={styles.heading}>{t('headingEdit')}</h2>

        {isConfirmOpen && (
          <div className={styles.confirmOverlay} onClick={() => !isSubmitting && setIsConfirmOpen(false)}>
            <div className={styles.confirmModal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={t('confirmAriaLabelEdit')}>
              <h3 className={styles.confirmTitle}>{t('confirmTitleEdit')}</h3>
              <p className={styles.confirmText}>{t('confirmTextEdit')}</p>
              <div className={styles.confirmList}>
                {getConfirmSections().map((section) => (
                  <div key={section.label} className={styles.confirmItem}>
                    <div className={styles.confirmLabel}>{section.label}</div>
                    <div className={styles.confirmValue}>{section.value}</div>
                  </div>
                ))}
              </div>
              <div className={styles.confirmActions}>
                <button type="button" className={styles.secondarySubmitButton} onClick={() => setIsConfirmOpen(false)} disabled={isSubmitting}>{t('confirmBack')}</button>
                <button type="button" onClick={submitPublish} disabled={isSubmitting}>{isSubmitting ? t('confirmUpdating') : t('confirmUpdate')}</button>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className={styles.form}>
          <fieldset>
            <legend>{t('legendFlyer')}</legend>
            {existingFlyers?.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, 140px)', gap: 12 }}>
                {existingFlyers.map(img => (
                  <div key={img.id} style={{ position: 'relative', border: `1px solid ${pendingDeleteIds.has(img.id) ? '#dc2626' : '#ddd'}`, borderRadius: 8, padding: 8, background: pendingDeleteIds.has(img.id) ? '#fef2f2' : '#fff' }}>
                    <img
                      src={img.url}
                      alt='flyer'
                      style={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 4, opacity: pendingDeleteIds.has(img.id) ? 0.4 : 1 }}
                    />
                    {pendingDeleteIds.has(img.id) ? (
                      <button
                        type="button"
                        onClick={() => handleCancelDelete(img.id)}
                        style={{ marginTop: 6, width: '100%', border: '1px solid #bbb', background: '#fff', color: '#555', borderRadius: 6, padding: '4px 0', fontWeight: 600, cursor: 'pointer', fontSize: 12 }}
                      >
                        {t('cancelDelete')}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleDeleteFlyer(img.id)}
                        disabled={isSubmitting}
                        style={{ marginTop: 6, width: '100%', border: '1px solid #dc2626', background: '#fff', color: '#dc2626', borderRadius: 6, padding: '4px 0', fontWeight: 600, cursor: isSubmitting ? 'not-allowed' : 'pointer', fontSize: 12 }}
                      >
                        {t('deleteFlyer')}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: '#666' }}>{t('flyerEmpty')}</div>
            )}
            {pendingDeleteIds.size > 0 && (
              <p style={{ fontSize: 13, color: '#dc2626', margin: '8px 0 0' }}>{t('pendingDeleteNote')}</p>
            )}
            <div style={{ marginTop: 12 }}>
              <div style={{ marginBottom: 6, fontSize: 14 }}>{t('flyerAddLabel')}</div>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFlyerUpload}
                ref={flyerInputRef}
                style={{ display: 'none' }}
              />
              <button
                type="button"
                onClick={() => flyerInputRef.current && flyerInputRef.current.click()}
                disabled={isSubmitting}
                style={{ border: '1px solid #bbb', background: '#fff', color: '#333', borderRadius: 8, padding: '8px 14px', fontWeight: 600, cursor: isSubmitting ? 'not-allowed' : 'pointer', fontSize: 14 }}
              >
                {t('labelFlyerSelect')}
              </button>
              {getFieldError('flyers') && <p className={styles.fieldError}>{getFieldError('flyers')}</p>}
            </div>

            {/* 未送信フライヤーのプレビュー */}
            {pendingFlyers.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 6 }}>{t('pendingFlyerNote')}</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, 140px)', gap: 12 }}>
                  {pendingFlyers.map((entry, i) => (
                    <div key={i} style={{ border: '1px solid #fbbf24', borderRadius: 8, padding: 8, background: '#fffbeb' }}>
                      <img
                        src={entry.previewUrl}
                        alt={`pending flyer ${i + 1}`}
                        style={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 4 }}
                      />
                      <button
                        type="button"
                        onClick={() => handleRemovePendingFlyer(i)}
                        disabled={isSubmitting}
                        style={{ marginTop: 6, width: '100%', border: '1px solid #bbb', background: '#fff', color: '#555', borderRadius: 6, padding: '4px 0', fontWeight: 600, cursor: isSubmitting ? 'not-allowed' : 'pointer', fontSize: 12 }}
                      >
                        {t('cancelPendingFlyer')}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </fieldset>
          <fieldset>
            <legend>{t('legendBasic')}</legend>
            <label>
              {t('labelEventName')}<input type="text" name="name" value={form.name} onChange={handleChange} className={getFieldError('name') ? styles.inputError : ''} required />
            </label>
            {getFieldError('name') && <p className={styles.fieldError}>{getFieldError('name')}</p>}
            <label>
              {t('labelGenre')}
              <input
                type="text"
                name="genre"
                value={form.genre}
                onChange={handleChange}
                list="event-genre-options"
                placeholder={t('placeholderGenre')}
                className={getFieldError('genre') ? styles.inputError : ''}
              />
              <datalist id="event-genre-options">
                {EVENT_GENRE_OPTIONS.map((genre) => (
                  <option key={genre} value={genre} />
                ))}
              </datalist>
            </label>
            {getFieldError('genre') && <p className={styles.fieldError}>{getFieldError('genre')}</p>}
            <label>
              {t('labelSlugReadonly')}
              <input type="text" name="slug" value={form.slug || ''} readOnly className={getFieldError('slug') ? styles.inputError : ''} />
              {getFieldError('slug') && <p className={styles.fieldError}>{getFieldError('slug')}</p>}
              <span style={{ fontSize: '12px', color: '#666', display: 'block', marginTop: '2px' }}>
                {t('noteSlugReadonly')}
              </span>
            </label>
            <label>
              {t('labelVenueName')}<input type="text" name="venue_name" value={form.venue_name} onChange={handleChange} className={getFieldError('venue_name') ? styles.inputError : ''} />
            </label>
            {getFieldError('venue_name') && <p className={styles.fieldError}>{getFieldError('venue_name')}</p>}
            <label>
              {t('labelAddress')}<input type="text" name="address" value={form.address} onChange={handleChange} className={getFieldError('address') ? styles.inputError : ''} required />
            </label>
            {getFieldError('address') && <p className={styles.fieldError}>{getFieldError('address')}</p>}
            {!form.venue_id && (
              <div>
                <label style={{ fontWeight: 500 }}>イベント会場の位置（任意）</label>
                <p style={{ fontSize: 12, color: '#6b7280', margin: '2px 0 4px' }}>登録済み会場を使わない場合、会場イベントMAPに表示する位置を設定できます</p>
                <MapLocationPicker
                  lat={form.lat}
                  lng={form.lng}
                  onChange={(lat, lng) => setForm(prev => ({ ...prev, lat, lng }))}
                />
              </div>
            )}
          </fieldset>
          <fieldset>
            <legend>{t('legendDate')}</legend>
            <label>
              {t('labelDate')}<input type="date" name="date" value={form.date} onChange={handleChange} className={getFieldError('date') ? styles.inputError : ''} disabled required />
            </label>
            {getFieldError('date') && <p className={styles.fieldError}>{getFieldError('date')}</p>}
            <label>
              {t('labelEndDate')}<input type="date" name="end_date" value={form.end_date} onChange={handleChange} className={getFieldError('end_date') ? styles.inputError : ''} disabled required />
            </label>
            {getFieldError('end_date') && <p className={styles.fieldError}>{getFieldError('end_date')}</p>}
            <label>
              {t('labelOpenTime')}<input type="time" name="open_time" value={form.open_time} onChange={handleChange} className={getFieldError('open_time') ? styles.inputError : ''} required />
            </label>
            {getFieldError('open_time') && <p className={styles.fieldError}>{getFieldError('open_time')}</p>}
            <label>
              {t('labelStartTime')}<input type="time" name="start_time" value={form.start_time} onChange={handleChange} className={getFieldError('start_time') ? styles.inputError : ''} required />
            </label>
            {getFieldError('start_time') && <p className={styles.fieldError}>{getFieldError('start_time')}</p>}
            <label>
              {t('labelEndTime')}<input type="time" name="end_time" value={form.end_time} onChange={handleChange} className={getFieldError('end_time') ? styles.inputError : ''} required />
            </label>
            {getFieldError('end_time') && <p className={styles.fieldError}>{getFieldError('end_time')}</p>}
          </fieldset>
          {/* イベント全体販売枚数フィールド削除（グループ単位へ移行） */}
          <fieldset>
            <legend>{t('legendEventType')}</legend>
            {isPastEvent(form.date, form.end_date) && (
              <p style={{ color: '#b45309', fontSize: '13px', marginBottom: '8px' }}>
                {t('notePastEvent')}
              </p>
            )}
            <label>
              {t('labelEventType')}
              <select
                name="event_type"
                value={form.event_type}
                onChange={handleChange}
                disabled={isPastEvent(form.date, form.end_date) || form.status === 'published'}
              >
                <option value="paid">{t('optionPaid')}</option>
                <option value="free">{t('optionFree')}</option>
              </select>
            </label>

            <div style={{ marginTop: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  name="donation_enabled"
                  checked={!!form.donation_enabled}
                  disabled={isPastEvent(form.date, form.end_date)}
                  onChange={(e) => setForm(prev => ({ ...prev, donation_enabled: e.target.checked }))}
                />
                {t('labelDonationEnabled')}
              </label>
            </div>

            {form.donation_enabled && (
              <div style={{ marginTop: '12px', paddingLeft: '8px', borderLeft: '3px solid #c7d2fe' }}>
                <label>
                  {t('labelDonationStart')}
                  <input
                    type="datetime-local"
                    name="donation_start_at"
                    value={form.donation_start_at}
                    onChange={handleChange}
                  />
                </label>
                <div style={{ marginTop: '8px', display: 'block', color: '#444' }}>
                  {t('noteDonationEndAuto')}
                </div>
              </div>
            )}
          </fieldset>
          <fieldset>
            <legend>{t('legendTickets')}</legend>
            {form.event_type === 'free' ? (
              <p style={{ color: '#16a34a', fontWeight: '600' }}>
                {t('freeEventNote')}
              </p>
            ) : (
              <>
            <label>
              {t('labelFeeBearer')}
              <select name="fee_bearer" value={form.fee_bearer} onChange={handleChange} className={getFieldError('fee_bearer') ? styles.inputError : ''} disabled>
                <option value="organizer">{t('optionOrganizer')}</option>
                <option value="buyer">{t('optionBuyer')}</option>
              </select>
              {getFieldError('fee_bearer') && <p className={styles.fieldError}>{getFieldError('fee_bearer')}</p>}
              <span style={{ fontSize: '12px', color: '#888', display: 'block', marginTop: '2px' }}>
                {t('noteFeeBearer')}
              </span>
            </label>
            {ticketPrices.map((tp, idx) => {
              const now = new Date();
              const isNewGroup = !tp.id; // 新規追加のグループは常に編集可能（開始前扱い）
              let saleStarted = false;
              let saleActive = false;
              let saleEnded = false;
              if (!isNewGroup) {
                // 開始可否は日時入力値ではなく、実販売開始（売上/予約実績）で判定
                saleStarted = !!tp.hasActualSales;
                const endAt = tp.price_end_date ? new Date(tp.price_end_date) : null;
                saleActive = saleStarted && (!endAt || now <= endAt);
                saleEnded = saleStarted && !!endAt && now > endAt;
              }
              return (
              <div key={idx} className={styles.ticketGroupCard}>
                <div className={styles.ticketGroupHeader}>
                  <strong>{t('ticketGroupTitle', { num: idx + 1 })}</strong>
                  <button type="button" onClick={() => removeTicketPrice(idx)} className={styles.dangerButton} disabled={saleActive || saleEnded}>{t('ticketGroupDelete')}</button>
                </div>

                <label>
                  {t('ticketGroupType')}
                  <select
                    value={tp.type === '' ? 'その他' : (['超早期購入','早期購入','通常購入'].includes(tp.type) ? tp.type : 'その他')}
                    onChange={e => {
                      if (e.target.value === 'その他') {
                        handleTicketPriceChange(idx, 'type', '');
                      } else {
                        handleTicketPriceChange(idx, 'type', e.target.value);
                      }
                    }}
                    disabled={saleActive || saleEnded}
                  >
                    <option value="">{t('optionSelectDefault')}</option>
                    <option value="超早期購入">{t('optionTypeUltraEarly')}</option>
                    <option value="早期購入">{t('optionTypeEarly')}</option>
                    <option value="通常購入">{t('optionTypeNormal')}</option>
                    <option value="その他">{t('optionTypeOther')}</option>
                  </select>
                  {(tp.type === '' || !['超早期購入','早期購入','通常購入'].includes(tp.type)) && (
                    <>
                      <input
                        type="text"
                        value={tp.type}
                        maxLength={30}
                        onChange={e => {
                          const val = e.target.value;
                          if (/[<>]/.test(val)) return;
                          handleTicketPriceChange(idx, 'type', val);
                        }}
                      placeholder={t('placeholderTicketGroupType')}
                            className={styles.inlineTypeInput}
                        disabled={saleActive || saleEnded}
                      />
                      {tp.type.length > 0 && /[<>]/.test(tp.type) && (
                            <span className={styles.inlineErrorText}>{t('noteInvalidChars')}</span>
                      )}
                    </>
                  )}
                </label>
                  <label>
                    {t('ticketGroupSaleLimit')}
                    <input
                      type="number"
                      value={tp.ticket_limit}
                      onChange={e => handleTicketPriceChange(idx, 'ticket_limit', e.target.value)}
                      placeholder={t('placeholderUnlimited')}
                      disabled={saleEnded}
                    />
                  </label>
                <div className={styles.ticketMetaGrid}>
                  <label>
                    {t('ticketGroupSaleStart')}
                    <input type="datetime-local" value={tp.price_date} onChange={e => handleTicketPriceChange(idx, 'price_date', e.target.value)} disabled={saleActive || saleEnded} />
                  </label>
                  <label>
                    {t('ticketGroupSaleEnd')}
                    <input type="datetime-local" value={tp.price_end_date} onChange={e => handleTicketPriceChange(idx, 'price_end_date', e.target.value)} disabled={saleEnded} />
                  </label>
                </div>
                <div className={styles.priceTypeArea}>
                  {tp.prices.map((pt, j) => (
                    <div key={j} className={styles.priceTypeRow}>
                      <label>
                        {t('priceTypeLabel')}
                        <select
                          value={pt.price_type === '' ? 'other' : (['通常','男性','女性','グループ','VIP'].includes(pt.price_type) ? pt.price_type : 'other')}
                          onChange={e => {
                            if (e.target.value === 'other') {
                              handlePriceTypeChange(idx, j, 'price_type', '');
                            } else {
                              handlePriceTypeChange(idx, j, 'price_type', e.target.value);
                            }
                          }}
                          disabled={saleActive || saleEnded}
                        >
                          <option value="通常">{t('optionPriceTypeNormal')}</option>
                          <option value="男性">{t('optionPriceTypeMale')}</option>
                          <option value="女性">{t('optionPriceTypeFemale')}</option>
                          <option value="グループ">{t('optionPriceTypeGroup')}</option>
                          <option value="VIP">{t('optionPriceTypeVIP')}</option>
                          <option value="other">{t('optionPriceTypeOther')}</option>
                        </select>
                        {(pt.price_type === '' || !['通常','男性','女性','グループ','VIP'].includes(pt.price_type)) && (
                          <>
                            <input
                              type="text"
                              value={pt.price_type}
                              maxLength={20}
                              onChange={e => {
                                const val = e.target.value;
                                if (/[<>]/.test(val)) return;
                                handlePriceTypeChange(idx, j, 'price_type', val);
                              }}
                              placeholder={t('placeholderPriceTypeInput')}
                              className={styles.inlineTypeInput}
                              disabled={saleActive || saleEnded}
                            />
                            {pt.price_type.length > 0 && /[<>]/.test(pt.price_type) && (
                              <span className={styles.inlineErrorText}>{t('noteInvalidChars')}</span>
                            )}
                          </>
                        )}
                      </label>
                      <label>
                        {t('priceAmountLabel')}
                        <input type="number" value={pt.price} onChange={e => handlePriceTypeChange(idx, j, 'price', e.target.value)} placeholder={t('placeholderPrice')} disabled={saleActive || saleEnded} />
                      </label>
                      {pt.price_type === 'グループ' && (
                        <label>
                          {t('priceGroupCountLabel')}
                          <input type="number" value={pt.min_people || ''} onChange={e => handlePriceTypeChange(idx, j, 'min_people', e.target.value)} placeholder={t('placeholderGroupCount')} disabled={saleActive || saleEnded} />
                        </label>
                      )}
                      <label>
                        {t('priceSaleLimitLabel')}
                        <input type="number" value={pt.ticket_limit} onChange={e => handlePriceTypeChange(idx, j, 'ticket_limit', e.target.value)} placeholder={t('placeholderUnlimited')} disabled={saleEnded} />
                      </label>
                      <div className={styles.priceTypeActions}>
                        <button type="button" onClick={() => removePriceType(idx, j)} className={styles.dangerButton} disabled={saleActive || saleEnded}>{t('priceTypeDelete')}</button>
                      </div>
                    </div>
                  ))}
                  <div className={styles.priceTypeActions}>
                    <button type="button" onClick={() => addPriceType(idx, '')} className={styles.secondaryButton} disabled={saleActive || saleEnded}>{t('addPriceType')}</button>
                  </div>
                </div>
              </div>
            );})}
            <button type="button" onClick={addTicketPrice} className={styles.secondaryButton}>{t('addTicketGroup')}</button>
              </>
            )}
          </fieldset>
          <fieldset>
            <legend>{t('legendArtists')}</legend>

            {/* 出演者グループ管理 */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>出演者グループ（任意）</span>
                <button type="button"
                  style={{ fontSize: 12, padding: '3px 10px', borderRadius: 6, border: '1px solid #bbb', background: '#f9fafb', cursor: 'pointer' }}
                  onClick={() => {
                    const key = crypto.randomUUID();
                    setPerformerGroups(prev => [...prev, { key, id: null, name: '', sort_order: prev.length }]);
                  }}>
                  ＋ グループを追加
                </button>
              </div>
              {performerGroups.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {performerGroups.map((g, gi) => (
                    <div key={g.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input type="text" value={g.name} placeholder="グループ名（例: DAY1, LIVE）"
                        style={{ flex: 1, padding: '4px 8px', borderRadius: 6, border: '1px solid #d1d5db' }}
                        onChange={e => setPerformerGroups(prev => prev.map((x, i) => i === gi ? { ...x, name: e.target.value } : x))} />
                      <button type="button"
                        style={{ fontSize: 12, padding: '3px 8px', borderRadius: 6, border: '1px solid #fca5a5', background: '#fef2f2', color: '#dc2626', cursor: 'pointer' }}
                        onClick={() => {
                          setPerformerGroups(prev => prev.filter((_, i) => i !== gi));
                          setArtists(prev => prev.map(a => a.group_key === g.key ? { ...a, group_key: null } : a));
                        }}>
                        削除
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.artistGuideBox}>
              <strong>{t('noteArtistGuideRegistered')}</strong>{t('noteArtistGuideText1')}<br />
              <strong>{t('noteArtistGuideUnregistered')}</strong>{t('noteArtistGuideText2')}
            </div>
            <label>
              {t('labelArtistSearch')}
              <input
                type="text"
                value={artistSearch}
                onChange={(e) => setArtistSearch(e.target.value)}
                placeholder={t('placeholderArtistSearch')}
              />
            </label>
            <p className={styles.artistHint}>{t('noteArtistHint')}</p>
            {artistSearch && artistResults.length > 0 && (
              <ul className={styles.searchResults}>
                {artistResults.map((a) => (
                  <li
                    key={a.id}
                    onClick={() => setSelectedArtist({ id: a.id, name: a.organization_name })}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className={styles.searchResultRow}>
                      <span>{a.organization_name}</span>
                      <button
                        type="button"
                        className={styles.searchAddButton}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          addArtistToList({ id: a.id, name: a.organization_name });
                          setSelectedArtist(null);
                          setArtistSearch('');
                          setArtistResults([]);
                        }}
                      >
                        {t('artistSearchAdd')}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {selectedArtist && (
              <div className={styles.selectedArtistBox}>
                <p className={styles.selectedArtistText}>{t('artistSelectedName', { name: selectedArtist.name })}</p>
                <button
                  className={styles.secondaryButton}
                  type="button"
                  onClick={() => {
                    addArtistToList(selectedArtist);
                    setSelectedArtist(null);
                    setArtistSearch('');
                    setArtistResults([]);
                  }}
                >
                  {t('artistAddSelected')}
                </button>
              </div>
            )}
            {artists.map((artist, i) => (
              <div key={i} className={styles.artistRow}>
                <label className={styles.artistRowLabel}>
                  <span className={styles.artistRowHeader}>
                    <span className={styles.artistRowTitle}>{t('artistNumber', { num: i + 1 })}</span>
                    <span className={`${styles.artistModeBadge} ${artist.id ? styles.artistModeRegistered : styles.artistModeManual}`}>
                      {artist.id ? t('artistBadgeRegistered') : t('artistBadgeManual')}
                    </span>
                  </span>
                  <input
                    type="text"
                    value={artist.name}
                    onChange={(e) => handleArtistValueChange(i, 'name', e.target.value)}
                    placeholder={artist.id ? t('placeholderArtistRegistered') : t('placeholderArtistManual')}
                  />
                </label>
                {performerGroups.length > 0 && (
                  <label style={{ fontSize: 13, marginBottom: 4, display: 'block' }}>
                    グループ
                    <select value={artist.group_key || ''}
                      onChange={e => handleArtistValueChange(i, 'group_key', e.target.value || null)}
                      style={{ marginLeft: 8, padding: '2px 6px', borderRadius: 4, border: '1px solid #d1d5db' }}>
                      <option value="">なし</option>
                      {performerGroups.filter(g => g.name).map(g => (
                        <option key={g.key} value={g.key}>{g.name}</option>
                      ))}
                    </select>
                  </label>
                )}
                <button
                  className={styles.dangerButton}
                  type="button"
                  onClick={() => {
                    const updated = [...artists];
                    updated.splice(i, 1);
                    setArtists(updated);
                  }}
                >
                  {t('artistDelete')}
                </button>
                {!artist.id && artist.name.trim() !== '' && (
                  <div className={styles.artistExtraFields}>
                    <div className={styles.artistExtraHint}>
                      {t('noteArtistExtraHint')}
                    </div>
                    <label className={styles.artistRowLabel}>
                      <span className={styles.artistRowTitle}>{t('artistDescTitle')}</span>
                      <textarea
                        value={artist.description || ''}
                        onChange={(e) => handleArtistValueChange(i, 'description', e.target.value)}
                        placeholder={t('placeholderArtistDesc')}
                        maxLength={2000}
                      />
                    </label>
                    <label className={styles.artistRowLabel}>
                      <span className={styles.artistRowTitle}>{t('artistPhotoTitle')}</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleArtistImageChange(i, e.target.files?.[0] || null)}
                      />
                    </label>
                    {(artist.imagePreview || artist.existingImageUrl) && (
                      <img
                        src={artist.imagePreview || artist.existingImageUrl}
                        alt={`${artist.name || 'artist'} preview`}
                        className={styles.artistImagePreview}
                      />
                    )}
                  </div>
                )}
                {!artist.id && artist.name.trim() === '' && (
                  <div className={styles.artistExtraCollapsed}>
                    {t('noteArtistCollapsed')}
                  </div>
                )}
              </div>
            ))}
            <div className={styles.artistActionRow}>
              <button
                className={styles.secondaryButton}
                type="button"
                onClick={() => setArtists([...artists, createEmptyArtist()])}
              >
                {t('artistAdd')}
              </button>
            </div>
          </fieldset>
          <fieldset>
            <legend>{t('legendDescription')}</legend>
            <span style={{ fontSize: '12px', color: '#888', display: 'block', marginBottom: '4px' }}>
              {t('noteDescriptionLength')}<br />
              <span style={{ color: '#888' }}>
                {t('noteDescriptionChars')}
              </span>
            </span>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              placeholder={t('placeholderDescription')}
              maxLength={3000}
              autoComplete="off"
              className={getFieldError('description') ? styles.inputError : ''}
            />
            {getFieldError('description') && <p className={styles.fieldError}>{getFieldError('description')}</p>}
          </fieldset>

          <fieldset className={styles.fieldset}>
            <legend className={styles.legend}>{t('legendContact')}</legend>
            <span style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
              {t('noteContact')}
            </span>
            <textarea
              name="contact_info"
              value={form.contact_info}
              onChange={handleChange}
              placeholder={t('placeholderContact')}
              maxLength={1000}
              autoComplete="off"
              required
              className={getFieldError('contact_info') ? styles.inputError : ''}
            />
            {getFieldError('contact_info') && <p className={styles.fieldError}>{getFieldError('contact_info')}</p>}
          </fieldset>
          <div className={styles.submitArea}>
            {getErrorSummary() && <p className={styles.error}>{getErrorSummary()}</p>}
            {success && <p style={{ color: '#16a34a', margin: '0 0 8px' }}>{success}</p>}
            <button type="submit" disabled={isSubmitting}>{t('btnUpdate')}</button>
            {getFieldError('submit') && <p className={styles.fieldError}>{getFieldError('submit')}</p>}
          </div>
        </form>
      </div>
    </>
  );
};

export default EventEditPage;
